import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const API_BASE = api.defaults.baseURL.replace(/\/api$/, '');

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

const STATUS_BADGE = {
  draft:'badge-gray', submitted:'badge-amber', mgr_approved:'badge-blue',
  hr_approved:'badge-teal', client_approved:'badge-green',
  rejected:'badge-red', needs_revision:'badge-amber'
};

const DAY_COLORS = {
  PRESENT:'#10b981', HALF_DAY:'#3b82f6', LEAVE:'#f59e0b',
  CLIENT_HOLIDAY:'#8b5cf6', WEEKLY_OFF:'#6b7280', COMP_OFF:'#ec4899'
};

export default function Reports() {
  const { user } = useAuth();
  const isHR  = user?.role === 'hr';
  const isMgr = user?.role === 'manager';
  const now = new Date();
  const [selYear,  setSelYear]  = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth());
  const [timesheets, setTimesheets] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  // Modal state
  const [selected,      setSelected]      = useState(null);
  const [comment,       setComment]       = useState('');
  const [clientBy,      setClientBy]      = useState('');
  const [saving,        setSaving]        = useState(false);
  const [editEntries,   setEditEntries]   = useState(false);
  const [editedEntries, setEditedEntries] = useState([]);
  const [allProjects,   setAllProjects]   = useState([]);
  const [savingEntries, setSavingEntries] = useState(false);
  const [emailModal,    setEmailModal]    = useState(false);
  const [emailTo,       setEmailTo]       = useState('');
  const [emailCc,       setEmailCc]       = useState('');
  const [emailSubject,  setEmailSubject]  = useState('');
  const [emailBody,     setEmailBody]     = useState('');
  const [sending,       setSending]       = useState(false);
  const [exportingEmpStatus, setExportingEmpStatus] = useState(false);
  const [exportingBulk, setExportingBulk] = useState(false);

  const years = [];
  for (let y = 2024; y <= now.getFullYear(); y++) years.push(y);

  const availableMonths = MONTHS.map((m, i) => ({ label: m, value: i }))
    .filter(m => selYear < now.getFullYear() || m.value <= now.getMonth());

  useEffect(() => {
    if (selYear === now.getFullYear() && selMonth > now.getMonth()) setSelMonth(now.getMonth());
  }, [selYear]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = isMgr
        ? `/timesheets/team?month=${selMonth + 1}&year=${selYear}`
        : `/timesheets?month=${selMonth + 1}&year=${selYear}`;
      const res = await api.get(url);
      setTimesheets(res.data.timesheets || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selYear, selMonth, isMgr]);

  useEffect(() => { load(); }, [load]);

  const timesheets_submitted = timesheets.filter(t => t.status !== 'draft');
  const total          = timesheets_submitted.length;
  const waitingHR      = timesheets_submitted.filter(t => ['submitted','mgr_approved'].includes(t.status)).length;
  const waitingClient  = timesheets_submitted.filter(t => t.status === 'hr_approved').length;
  const clientApproved = timesheets_submitted.filter(t => t.status === 'client_approved').length;
  const rejected       = timesheets_submitted.filter(t => ['rejected','needs_revision'].includes(t.status)).length;

  const kpis = [
    { label:'Total Submitted',     value:total,         color:'#6c63ff', icon:'📋', sub:'this month',     filter:'all' },
    { label:'Waiting — HR',        value:waitingHR,     color:'#f59e0b', icon:'🏢', sub:'pending review', filter:'submitted' },
    { label:'Waiting — Client',    value:waitingClient, color:'#8b5cf6', icon:'📨', sub:'hr approved',    filter:'hr_approved' },
    { label:'Client Approved',     value:clientApproved,color:'#10b981', icon:'✅', sub:'fully approved', filter:'client_approved' },
    { label:'Rejected / Revision', value:rejected,      color:'#ef4444', icon:'❌', sub:'needs action',   filter:'rejected' },
  ];

  const statusBadge = {
    submitted:       { label:'Submitted',       color:'#f59e0b' },
    mgr_approved:    { label:'Mgr Approved',    color:'#3b82f6' },
    hr_approved:     { label:'HR Approved',     color:'#4ecdc4' },
    client_approved: { label:'Client Approved', color:'#10b981' },
    rejected:        { label:'Rejected',        color:'#ef4444' },
    needs_revision:  { label:'Needs Revision',  color:'#f59e0b' },
  };

  const displayList = activeFilter === null || activeFilter === 'all'
    ? timesheets_submitted
    : activeFilter === 'rejected'
      ? timesheets_submitted.filter(t => ['rejected','needs_revision'].includes(t.status))
      : activeFilter === 'submitted'
        ? timesheets_submitted.filter(t => ['submitted','mgr_approved'].includes(t.status))
        : timesheets_submitted.filter(t => t.status === activeFilter);

  const totalHours = (ts) =>
    (ts.entries || []).reduce((s,e) =>
      s + (e.day_type==='PRESENT' ? parseFloat(e.hours||0) : e.day_type==='HALF_DAY' ? 4 : 0), 0
    ).toFixed(1);

  const formatMonth = (dateStr) => { try { return format(new Date(dateStr),'MMMM yyyy'); } catch { return dateStr; } };

  // ── Open review modal ────────────────────────────────────────────────────
  const openReview = async (ts) => {
    setSelected(ts);
    setComment(ts.hr_comments || ts.mgr_comments || '');
    setClientBy(ts.client_approved_by || '');
    setEditEntries(false); setEditedEntries([]);
    if (!allProjects.length) {
      try { const r = await api.get('/projects'); setAllProjects(r.data.projects || []); } catch {}
    }
  };

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleHRReview = async (action) => {
    setSaving(true);
    try {
      await api.put(`/timesheets/${selected.id}/hr-review`, { action, comments: comment });
      toast.success(action==='approve' ? 'HR approved ✓' : action==='request_revision' ? 'Revision requested' : 'Rejected');
      setSelected(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const handleClientReview = async (action) => {
    setSaving(true);
    try {
      await api.put(`/timesheets/${selected.id}/client-review`, { action, client_approved_by: clientBy, comments: comment });
      toast.success(`Client ${action==='approve' ? 'approved ✅' : 'rejected ❌'}`);
      setSelected(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const handleSendToClient = async () => {
    setSaving(true);
    try {
      await api.put(`/timesheets/${selected.id}/send-to-client`);
      toast.success('Marked as sent to client!');
      const refreshed = await api.get(`/timesheets?month=${selMonth+1}&year=${selYear}`);
      const latest = (refreshed.data.timesheets||[]).find(t => t.id === selected.id);
      if (latest) setSelected(latest); else { setSelected(null); load(); }
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const handleDownload = async (ts) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/timesheets/${ts.id}/download-excel`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { alert('Download failed.'); return; }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `Timesheet_${ts.employee?.name?.replace(/\s+/g,'_')}_${formatMonth(ts.week_start).replace(' ','_')}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Download error.'); }
  };

  const openEmailModal = async (ts) => {
    try {
      const res = await api.get(`/timesheets/${ts.id}/email-preview`);
      setEmailSubject(res.data.subject);
      setEmailBody(res.data.body);
      setEmailTo(res.data.client_email || '');
      setEmailCc('');
      setEmailModal(true);
    } catch { toast.error('Failed to load email preview.'); }
  };

  const handleSendEmail = async () => {
    if (!emailTo.trim()) { toast.error('Enter recipient email.'); return; }
    setSending(true);
    try {
      await api.post(`/timesheets/${selected.id}/send-email`, {
        to: emailTo.trim(),
        cc: emailCc.trim() || undefined,
        subject: emailSubject,
        body: emailBody,
      });
      toast.success(`Emailed to ${emailTo} ✅`);
      setEmailModal(false); setSelected(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSending(false); }
  };

  const saveHREntries = async () => {
    setSavingEntries(true);
    try {
      const entriesToSave = editedEntries.filter(e => !e._deleted).map(({ _deleted, ...e }) => e);
      await api.put(`/timesheets/${selected.id}/hr-edit-entries`, { entries: entriesToSave });
      toast.success('Entries updated!');
      setEditEntries(false);
      const r = await api.get(`/timesheets?month=${selMonth+1}&year=${selYear}`);
      const updated = (r.data.timesheets||[]).find(t => t.id === selected.id);
      if (updated) setSelected(updated);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSavingEntries(false); }
  };

  const canHRReview     = isHR && ['submitted','mgr_approved'].includes(selected?.status);
  const canSendEmail    = isHR && selected?.status === 'hr_approved';
  const canHREdit       = isHR && ['submitted','mgr_approved','hr_approved'].includes(selected?.status) && selected?.status !== 'client_approved';
  const canClientReview = isHR && selected?.status === 'hr_approved' && !!selected?.sent_to_client_at;
  const canSendToClient = isHR && selected?.status === 'hr_approved' && !selected?.sent_to_client_at;

  const handleExportEmployeeStatus = async () => {
    setExportingEmpStatus(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/reports/employee-status-excel?month=${selMonth + 1}&year=${selYear}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { alert('Export failed'); return; }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `Employee_Status_${MONTHS[selMonth]}_${selYear}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Export error'); }
    finally { setExportingEmpStatus(false); }
  };

  const handleExportBulkTimesheets = async () => {
    setExportingBulk(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/reports/bulk-timesheets-zip?month=${selMonth + 1}&year=${selYear}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.message || 'Export failed');
        return;
      }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `Timesheets_${MONTHS[selMonth]}_${selYear}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Export error'); }
    finally { setExportingBulk(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{isMgr ? 'Team Timesheets' : 'Reports'}</h1>
          <p className="page-sub">
            {isMgr
              ? `${MONTHS[selMonth]} ${selYear} — View your team's timesheet status (approvals are handled by HR)`
              : `${MONTHS[selMonth]} ${selYear} — Timesheet Status Overview`}
          </p>
        </div>
        {isHR && (
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-secondary" disabled={exportingBulk} onClick={handleExportBulkTimesheets}>
              {exportingBulk ? 'Zipping…' : '📦 Download HR-Approved Timesheets'}
            </button>
            <button className="btn btn-primary" disabled={exportingEmpStatus} onClick={handleExportEmployeeStatus}>
              {exportingEmpStatus ? 'Exporting…' : '📥 Export Employee Status'}
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card" style={{ padding:'14px 18px', marginBottom:20, display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
        <div className="field" style={{ flex:'0 0 auto', marginBottom:0 }}>
          <label>Year</label>
          <select value={selYear} onChange={e => setSelYear(Number(e.target.value))} style={{ minWidth:90 }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="field" style={{ flex:'0 0 auto', marginBottom:0 }}>
          <label>Month</label>
          <select value={selMonth} onChange={e => setSelMonth(Number(e.target.value))} style={{ minWidth:130 }}>
            {availableMonths.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div style={{ fontSize:13, color:'#6b7280', marginTop:18 }}>{total} timesheet{total!==1?'s':''} found</div>
      </div>

      {loading ? <div className="loading">Loading…</div> : (
        <>
          {/* KPI Cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:24 }}>
            {kpis.map(k => {
              const isActive = activeFilter === k.filter || (activeFilter === null && k.filter === 'all');
              return (
                <div key={k.label} className="card"
                  onClick={() => setActiveFilter(activeFilter === k.filter ? null : k.filter)}
                  style={{ padding:'18px 20px', borderLeft:`3px solid ${k.color}`, cursor:'pointer', transition:'all .15s',
                    background: isActive ? `${k.color}15` : undefined,
                    boxShadow: isActive ? `0 0 0 1px ${k.color}55` : undefined }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                    <div>
                      <div style={{ fontSize:12, color:'#6b7280', marginBottom:6 }}>{k.label}</div>
                      <div style={{ fontSize:32, fontWeight:700, color:k.color, lineHeight:1 }}>{k.value}</div>
                      <div style={{ fontSize:11, color:'#4b5563', marginTop:4 }}>{k.sub}</div>
                    </div>
                    <div style={{ fontSize:22 }}>{k.icon}</div>
                  </div>
                  {isActive && <div style={{ fontSize:10, color:k.color, marginTop:8, fontWeight:600 }}>● Filtered</div>}
                </div>
              );
            })}
          </div>

          {/* Table */}
          {displayList.length === 0 ? (
            <div className="card"><div className="empty"><div className="empty-icon">📊</div><p>No timesheets found.</p></div></div>
          ) : (
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <div style={{ padding:'14px 20px', borderBottom:'1px solid #2a2d3a', fontWeight:600, fontSize:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span>{activeFilter && activeFilter !== 'all' ? kpis.find(k=>k.filter===activeFilter)?.label : 'All Timesheets'} — {MONTHS[selMonth]} {selYear}</span>
                {activeFilter && activeFilter !== 'all' && (
                  <button onClick={() => setActiveFilter(null)} style={{ fontSize:12, color:'#6b7280', background:'none', border:'1px solid #2a2d3a', borderRadius:6, padding:'3px 10px', cursor:'pointer' }}>Clear ✕</button>
                )}
              </div>
              <div style={{ overflowX:'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>#</th><th>Employee</th><th>Department</th><th>Status</th>
                      <th>Billable Hrs</th><th>Submitted</th><th>Last Updated</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayList.sort((a,b) => {
                      const order = {client_approved:0,hr_approved:1,mgr_approved:2,submitted:3,rejected:4,needs_revision:5};
                      return (order[a.status]??9)-(order[b.status]??9);
                    }).map((ts, idx) => {
                      const st = statusBadge[ts.status] || { label:ts.status, color:'#6b7280' };
                      return (
                        <tr key={ts.id}>
                          <td style={{ color:'#6b7280', fontSize:12 }}>{idx+1}</td>
                          <td>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <div className="avatar" style={{ width:28, height:28, fontSize:11 }}>{ts.employee?.name?.charAt(0)}</div>
                              <div>
                                <div style={{ fontWeight:600, fontSize:13 }}>{ts.employee?.name}</div>
                                {ts.employee?.employee_code && <div style={{ fontSize:11, color:'#6c63ff' }}>#{ts.employee.employee_code}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="text-sm text-muted">{ts.employee?.department||'—'}</td>
                          <td>
                            <span style={{ fontSize:11.5, fontWeight:600, color:st.color, background:`${st.color}18`, padding:'3px 10px', borderRadius:20, border:`1px solid ${st.color}33` }}>
                              {st.label}
                            </span>
                          </td>
                          <td style={{ fontWeight:700, color:'#6c63ff', textAlign:'center' }}>{totalHours(ts)}</td>
                          <td className="text-sm text-muted">{ts.submitted_at ? format(new Date(ts.submitted_at),'MMM d') : '—'}</td>
                          <td className="text-sm text-muted">{format(new Date(ts.updated_at||ts.created_at),'MMM d, h:mm a')}</td>
                          <td>
                            <button className="btn btn-secondary btn-sm" onClick={() => openReview(ts)}>View</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Review Modal ── */}
      {selected && (
        <div className="overlay" onClick={() => setSelected(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
              <div>
                <div className="modal-title" style={{ marginBottom:2 }}>{selected.employee?.name} — {formatMonth(selected.week_start)}</div>
                <span className={`badge ${STATUS_BADGE[selected.status]}`}>{selected.status.replace(/_/g,' ')}</span>
              </div>
              <button onClick={() => setSelected(null)} style={{ background:'none', border:'none', color:'#6b7280', cursor:'pointer', fontSize:20 }}>✕</button>
            </div>

            {/* Trail chips */}
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', margin:'12px 0' }}>
              {selected.submitted_at && <span className="chip">📤 Submitted {format(new Date(selected.submitted_at),'MMM d')}</span>}
              {selected.mgr_reviewed_at && <span className="chip">👤 Manager approved {format(new Date(selected.mgr_reviewed_at),'MMM d')}</span>}
              {selected.hr_reviewed_at && <span className="chip">🏢 HR reviewed {format(new Date(selected.hr_reviewed_at),'MMM d')}</span>}
              {selected.sent_to_client_at && <span className="chip" style={{ background:'rgba(78,205,196,.1)', color:'#4ecdc4', border:'1px solid rgba(78,205,196,.2)' }}>📨 Sent to client {format(new Date(selected.sent_to_client_at),'MMM d')}</span>}
              {selected.client_approved_at && (
                <span className="chip" style={{ background:selected.status==='client_approved'?'rgba(16,185,129,.1)':'rgba(239,68,68,.1)', color:selected.status==='client_approved'?'#10b981':'#ef4444', border:`1px solid ${selected.status==='client_approved'?'rgba(16,185,129,.2)':'rgba(239,68,68,.2)'}` }}>
                  {selected.status==='client_approved'?'✅':'❌'} {selected.client_approved_by} {format(new Date(selected.client_approved_at),'MMM d')}
                </span>
              )}
            </div>

            {/* Entries table with edit */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <span style={{ fontSize:12.5, color:'#6b7280' }}>Timesheet Entries</span>
              {canHREdit && !editEntries && (
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  setEditedEntries((selected.entries||[]).map(e => ({ id:e.id, date:e.date, day_type:e.day_type, project_id:e.project_id||'', hours:e.hours||'', description:e.description||'' })));
                  setEditEntries(true);
                }}>✏ Edit Entries</button>
              )}
              {editEntries && (
                <div style={{ display:'flex', gap:6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditEntries(false)}>Cancel</button>
                  <button className="btn btn-primary btn-sm" disabled={savingEntries} onClick={saveHREntries}>{savingEntries?'Saving…':'✓ Save'}</button>
                </div>
              )}
            </div>

            <div style={{ background:'#0f1117', borderRadius:10, marginBottom:14, border:`1px solid ${editEntries?'#6c63ff':'#2a2d3a'}`, maxHeight:280, overflowY:'auto', overflowX:'auto' }}>
              <table>
                <thead><tr><th>Date</th><th>Type</th><th>Project</th><th>Hours</th><th>Description</th>{editEntries && <th style={{ width:40 }}></th>}</tr></thead>
                <tbody>
                  {!(selected.entries||[]).length ? (
                    <tr><td colSpan={5} style={{ textAlign:'center', color:'#6b7280' }}>No entries</td></tr>
                  ) : !editEntries ? (
                    (selected.entries||[]).map(e => (
                      <tr key={e.id}>
                        <td style={{ whiteSpace:'nowrap' }}>{format(new Date(e.date),'EEE, MMM d')}</td>
                        <td><span className="badge" style={{ background:`${DAY_COLORS[e.day_type]}22`, color:DAY_COLORS[e.day_type] }}>{e.day_type.replace(/_/g,' ')}</span></td>
                        <td className="text-sm">{e.project?.name||'—'}</td>
                        <td><strong>{e.day_type==='HALF_DAY'?'4.0':e.hours||'—'}</strong></td>
                        <td className="text-sm text-muted">{e.description||'—'}</td>
                      </tr>
                    ))
                  ) : editedEntries.map((e,idx) => (
                    <tr key={e.id||idx} style={{ opacity: e._deleted ? 0.35 : 1 }}>
                      <td style={{ whiteSpace:'nowrap', fontSize:12, textDecoration: e._deleted ? 'line-through' : 'none' }}>{format(new Date(e.date),'EEE, MMM d')}</td>
                      <td>
                        <select value={e.day_type}
                          onChange={ev => { const u=[...editedEntries]; u[idx]={...e,day_type:ev.target.value,project_id:['PRESENT','HALF_DAY'].includes(ev.target.value)?e.project_id:'',hours:ev.target.value==='PRESENT'?e.hours||'8':ev.target.value==='HALF_DAY'?'4':''}; setEditedEntries(u); }}
                          style={{ background:'#1a1d27', border:`1px solid ${DAY_COLORS[e.day_type]||'#2a2d3a'}55`, borderRadius:6, color:DAY_COLORS[e.day_type]||'#fff', fontSize:11, fontWeight:600, padding:'3px 6px', outline:'none' }}>
                          {['PRESENT','HALF_DAY','LEAVE','CLIENT_HOLIDAY','WEEKLY_OFF','COMP_OFF'].map(dt=><option key={dt} value={dt}>{dt.replace(/_/g,' ')}</option>)}
                        </select>
                      </td>
                      <td>
                        {['PRESENT','HALF_DAY'].includes(e.day_type) ? (
                          <select value={e.project_id}
                            onChange={ev => { const u=[...editedEntries]; u[idx]={...e,project_id:ev.target.value}; setEditedEntries(u); }}
                            style={{ background:'#0f1117', border:'1px solid #2a2d3a', borderRadius:6, color:'#d1d5db', fontSize:11, padding:'3px 6px', outline:'none', maxWidth:120 }}>
                            <option value="">Select…</option>
                            {allProjects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        ) : <span style={{ color:'#6b7280', fontSize:12 }}>—</span>}
                      </td>
                      <td>
                        {e.day_type==='PRESENT' ? (
                          <input type="number" min="0.5" max="24" step="0.5" value={e.hours}
                            onChange={ev => { const u=[...editedEntries]; u[idx]={...e,hours:ev.target.value}; setEditedEntries(u); }}
                            style={{ background:'#0f1117', border:'1px solid #2a2d3a', borderRadius:6, color:'#fff', fontSize:12, padding:'3px 6px', width:56, outline:'none' }}/>
                        ) : <strong style={{ color:'#9ca3af', fontSize:12 }}>{e.day_type==='HALF_DAY'?'4.0':'—'}</strong>}
                      </td>
                      <td>
                        <input value={e.description} disabled={e._deleted}
                          onChange={ev => { const u=[...editedEntries]; u[idx]={...e,description:ev.target.value}; setEditedEntries(u); }}
                          placeholder="Description…"
                          style={{ background:'#0f1117', border:'1px solid #2a2d3a', borderRadius:6, color:'#9ca3af', fontSize:11, padding:'3px 7px', width:'100%', minWidth:100, outline:'none' }}/>
                      </td>
                      <td>
                        <button
                          onClick={() => { const u=[...editedEntries]; u[idx]={...e,_deleted:!e._deleted}; setEditedEntries(u); }}
                          title={e._deleted ? 'Restore this entry' : 'Delete this entry'}
                          style={{ background:'none', border:'none', cursor:'pointer', fontSize:14, color: e._deleted ? '#10b981' : '#ef4444', padding:'4px 6px' }}
                        >
                          {e._deleted ? '↺' : '🗑'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary boxes */}
            {(() => {
              const entries = selected.entries||[];
              const count = t => entries.filter(e=>e.day_type===t).length;
              const hrs = entries.reduce((s,e)=>s+(e.day_type==='PRESENT'?parseFloat(e.hours||0):e.day_type==='HALF_DAY'?4:0),0);
              return (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(90px,1fr))', gap:8, marginBottom:14 }}>
                  {[['Present',count('PRESENT'),'#10b981'],['Half Day',count('HALF_DAY'),'#3b82f6'],['Leave',count('LEAVE'),'#f59e0b'],['Holiday',count('CLIENT_HOLIDAY'),'#8b5cf6'],['Off',count('WEEKLY_OFF')+count('COMP_OFF'),'#6b7280'],['Bill Hrs',hrs.toFixed(1)+'h','#6c63ff']].map(([l,v,c])=>(
                    <div key={l} style={{ background:'#0f1117', border:'1px solid #2a2d3a', borderRadius:8, padding:'8px 10px', textAlign:'center' }}>
                      <div style={{ fontSize:10.5, color:'#6b7280', marginBottom:3 }}>{l}</div>
                      <div style={{ fontSize:17, fontWeight:700, color:c }}>{v}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Stage indicators */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6, marginBottom:16 }}>
              {[['1','Employee',['draft','submitted','mgr_approved','hr_approved','client_approved','rejected','needs_revision']],['2','HR',['hr_approved','client_approved']],['3','Client',['client_approved']]].map(([num,label,done])=>{
                const isDone=done.includes(selected.status);
                const isCurr=(label==='HR'&&['submitted','mgr_approved'].includes(selected.status))||(label==='Client'&&selected.status==='hr_approved')||(label==='Employee'&&['draft','needs_revision','rejected'].includes(selected.status));
                return (
                  <div key={num} style={{ textAlign:'center', padding:'8px 4px', borderRadius:8, background:isCurr?'rgba(108,99,255,.12)':isDone?'rgba(16,185,129,.08)':'#0f1117', border:`1px solid ${isCurr?'#6c63ff':isDone?'#10b981':'#2a2d3a'}` }}>
                    <div style={{ fontSize:11, color:isCurr?'#6c63ff':isDone?'#10b981':'#6b7280', fontWeight:600 }}>{isDone&&!isCurr?'✓':num}</div>
                    <div style={{ fontSize:11, color:isCurr?'#6c63ff':isDone?'#10b981':'#6b7280', marginTop:2 }}>{label}</div>
                  </div>
                );
              })}
            </div>

            {canClientReview && (
              <div className="field" style={{ marginBottom:12 }}>
                <label>Client Contact Name <span style={{ color:'#6b7280', fontWeight:400 }}>(optional)</span></label>
                <input value={clientBy} onChange={e=>setClientBy(e.target.value)} placeholder="e.g. John Smith"/>
              </div>
            )}

            {(canHRReview||canSendToClient||canClientReview) && (
              <div className="field" style={{ marginBottom:16 }}>
                <label>Comments (optional)</label>
                <textarea rows={2} value={comment} onChange={e=>setComment(e.target.value)} placeholder="Add notes…"/>
              </div>
            )}

            {!canHRReview && !canSendToClient && !canClientReview && (
              (selected.mgr_comments||selected.hr_comments||selected.client_comments) && (
                <div style={{ padding:'12px 14px', background:'#0f1117', borderRadius:8, marginBottom:16, display:'flex', flexDirection:'column', gap:6 }}>
                  {selected.mgr_comments    && <div style={{ fontSize:12.5 }}><span style={{ color:'#6b7280' }}>Manager: </span>{selected.mgr_comments}</div>}
                  {selected.hr_comments     && <div style={{ fontSize:12.5 }}><span style={{ color:'#6b7280' }}>HR: </span>{selected.hr_comments}</div>}
                  {selected.client_comments && <div style={{ fontSize:12.5 }}><span style={{ color:'#6b7280' }}>Client: </span>{selected.client_comments}</div>}
                </div>
              )
            )}

            {/* Action buttons */}
            <div className="modal-footer" style={{ flexWrap:'wrap', gap:8 }}>
              <button className="btn btn-secondary" onClick={()=>setSelected(null)}>Close</button>
              {isHR && <button className="btn btn-secondary" onClick={()=>handleDownload(selected)}>📥 Download Excel</button>}

              {canHRReview && (
                <>
                  <button className="btn btn-danger" disabled={saving} onClick={()=>handleHRReview('reject')}>✕ Reject</button>
                  <button className="btn btn-secondary" disabled={saving} onClick={()=>handleHRReview('request_revision')}>↩ Request Revision</button>
                  <button className="btn btn-primary" disabled={saving} onClick={()=>handleHRReview('approve')}>✓ HR Approve → Send to Client</button>
                </>
              )}
              {canSendEmail && (
                <button className="btn btn-primary" disabled={saving} onClick={()=>openEmailModal(selected)}>📧 Send Email to Client</button>
              )}
              {canSendToClient && (
                <button className="btn btn-primary" disabled={saving} onClick={handleSendToClient}>📨 Mark as Sent to Client</button>
              )}
              {canClientReview && (
                <>
                  <button className="btn btn-danger" disabled={saving} onClick={()=>handleClientReview('reject')}>❌ Client Rejected</button>
                  <button className="btn btn-success" disabled={saving} onClick={()=>handleClientReview('approve')}>✅ Client Approved</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {emailModal && selected && (
        <div className="overlay" onClick={()=>setEmailModal(false)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()} style={{ maxWidth:620 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div className="modal-title" style={{ marginBottom:0 }}>📧 Send Timesheet to Client</div>
              <button onClick={()=>setEmailModal(false)} style={{ background:'none', border:'none', color:'#6b7280', cursor:'pointer', fontSize:20 }}>✕</button>
            </div>
            <div className="form-grid">
              <div className="field">
                <label>Subject</label>
                <input value={emailSubject} onChange={e=>setEmailSubject(e.target.value)} style={{ fontWeight:500 }}/>
              </div>
              <div className="field">
                <label>To * <span style={{ fontWeight:400, color:'#6b7280' }}>(client email)</span></label>
                <input type="email" value={emailTo} onChange={e=>setEmailTo(e.target.value)} placeholder="client@company.com"/>
              </div>
              <div className="field">
                <label>CC <span style={{ fontWeight:400, color:'#6b7280' }}>(optional)</span></label>
                <input value={emailCc} onChange={e=>setEmailCc(e.target.value)} placeholder="cc@company.com"/>
              </div>
              <div className="field">
                <label>Email Body <span style={{ fontWeight:400, color:'#6b7280' }}>(click to edit before sending)</span></label>
                <div
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={e => setEmailBody(e.currentTarget.innerHTML)}
                  style={{ background:'#0f1117', border:'1px solid #2a2d3a', borderRadius:8, padding:'14px 16px', fontSize:13, color:'#d1d5db', lineHeight:1.7, maxHeight:260, overflowY:'auto', outline:'none', cursor:'text' }}
                  dangerouslySetInnerHTML={{ __html: emailBody }}/>
              </div>
              <div style={{ padding:'10px 14px', background:'rgba(108,99,255,.08)', border:'1px solid rgba(108,99,255,.2)', borderRadius:8, fontSize:12.5, color:'#9ca3af' }}>
                📎 The timesheet Excel file will be attached automatically.
              </div>
            </div>
            <div className="modal-footer" style={{ marginTop:20 }}>
              <button className="btn btn-secondary" onClick={()=>setEmailModal(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={sending||!emailTo.trim()} onClick={handleSendEmail}>
                {sending?'Sending…':'📧 Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}